import type { IPropertyBag } from '@deity-io/di-data-basics';
import type { StructureEditor } from '@deity-io/di-data-structure-editor';
import type { ICollectionType, ISchema } from 'contracts/interfaces';
import React from 'react';

export enum MappingActionTypes {
  SELECT = 'SELECT',
  INIT_MAPPING = 'INIT_MAPPING',
  INIT_MAPPING_SUMMARY = 'INIT_MAPPING_SUMMARY',
  UPDATE_DEFAULT = 'UPDATE_DEFAULT',
  UPDATE_TARGET_PROPERTY = 'UPDATE_TARGET_PROPERTY',
  NEW_TARGET_PROPERTY = 'NEW_TARGET_PROPERTY',
  REMOVE_TARGET_PROPERTY = 'REMOVE_TARGET_PROPERTY',
  // UPDATE_SUMMARY = 'UPDATE_SUMMARY',
  UPDATE_MAPPING_SCHEMA = 'UPDATE_MAPPING_SCHEMA',
  CONNECT = 'CONNECT',
  DISCONNECT = 'DISCONNECT',
  CREATE_SOURCE_MAPPING = 'CREATE_SOURCE_MAPPING',
  CREATE_OTHER_SOURCE_MAPPING = 'CREATE_OTHER_SOURCE_MAPPING',
  CREATE_TARGET_MAPPING = 'CREATE_TARGET_MAPPING',
  MAPPING_DISCONNECT_SOURCE = 'MAPPING_DISCONNECT_SOURCE',
  MAPPING_DISCONNECT_TARGET = 'MAPPING_DISCONNECT_TARGET',
  MAPPING_REMOVE_SOURCE = 'MAPPING_REMOVE_SOURCE',
  MAPPING_REMOVE_TARGET = 'MAPPING_REMOVE_TARGET',
  CLEAR = 'CLEAR',
  CREATE_SOURCE_PROCESSOR = 'CREATE_SOURCE_PROCESSOR'
}
interface IDataItem<T> extends IEntityType {
  schema: T;
}
interface IEntityType {
  uuid: string;
  packageVersionUuid: string;
  name: string;
  description?: string | null;
}

export interface IMappingEdge {
  sourcePath: string[];
  targetPath: string[];
}

// export interface ISelectedEdge {
//   type: MappingActionTypes.SELECT_EDGE;
//   payload: IMappingEdge | null;
// }

export interface ISelectedPayload {
  origin: 'source' | 'target' | 'edge';
  source?: string[];
  target?: string[];
  mapping?: string[];
}

export interface IClearState {
  type: MappingActionTypes.CLEAR;
}

export interface ISelected {
  type: MappingActionTypes.SELECT;
  payload?: ISelectedPayload;
}

export interface ICreateSourceProcessor {
  type: MappingActionTypes.CREATE_SOURCE_PROCESSOR;
  payload: { path: string[]; expression: string; type: string; fields: Array<string> };
}

// export interface ISelectedField {
//   type: MappingActionTypes.SELECT_SOURCE_FIELD | MappingActionTypes.SELECT_TARGET_FIELD;
//   payload: string[] | null;
// }

export interface InitMappingSchema {
  type: MappingActionTypes.INIT_MAPPING;
  payload: {
    mapping: IDataItem<ICollectionType>;
    source: IDataItem<ISchema>;
    target: IDataItem<ISchema>;
    locked?: boolean;
  };
}

export interface UpdateMappingSchema {
  type: MappingActionTypes.UPDATE_MAPPING_SCHEMA;
  payload: ICollectionType;
}

export interface IConnectPayload {
  edge: IMappingEdge;
}

export interface IConnectFields {
  type: MappingActionTypes.CONNECT;
  payload: IConnectPayload;
}

export interface IDisconnectFields {
  type: MappingActionTypes.DISCONNECT;
  payload: IConnectPayload;
}

export interface IUpdateDefault {
  type: MappingActionTypes.UPDATE_DEFAULT;
  payload: { path: string[]; property: string[]; value: any; regenerateHash?: boolean };
}

export interface IUpdateTargetProperty {
  type: MappingActionTypes.UPDATE_TARGET_PROPERTY;
  payload: { path: string[]; property: string[]; value: any; regenerateHash?: boolean };
}

export interface INewTargetProperty {
  type: MappingActionTypes.NEW_TARGET_PROPERTY;
  payload: { path: string[]; regenerateHash?: boolean };
}

export interface IRemoveTargetProperty {
  type: MappingActionTypes.REMOVE_TARGET_PROPERTY;
  payload: { path: string[]; index: number; regenerateHash?: boolean };
}

export interface IInitSummary {
  type: MappingActionTypes.INIT_MAPPING_SUMMARY;
  payload: IMappingSummaryType;
}

export interface ITogglePropertyPayload {
  set: boolean;
  property: string;
  fieldPath: string[];
  initValue: any;
  isSourceField: boolean;
}

// export interface IUpdateMappingSummary {
//   type: MappingActionTypes.UPDATE_SUMMARY;
//   payload: {
//     mappingTitle?: string;
//     sourceTitle?: string;
//     targetTitle?: string;
//     mapping?: number;
//     transformation?: number;
//   };
// }

export interface ICreateSourceMapping {
  type: MappingActionTypes.CREATE_SOURCE_MAPPING;
  payload: { sourcePath: string };
}

export interface ICreateOtherSourceMapping {
  type: MappingActionTypes.CREATE_OTHER_SOURCE_MAPPING;
  payload: { path: string[] };
}

export interface ICreateDanglingMapping {
  type: MappingActionTypes.CREATE_SOURCE_MAPPING | MappingActionTypes.CREATE_TARGET_MAPPING;
  payload: { path: string[] };
}

export interface IDisconnectMapping {
  type: MappingActionTypes.MAPPING_DISCONNECT_SOURCE | MappingActionTypes.MAPPING_DISCONNECT_TARGET;
  payload: { path: string[] };
}

export interface IRemoveMapping {
  type: MappingActionTypes.MAPPING_REMOVE_SOURCE | MappingActionTypes.MAPPING_REMOVE_TARGET;
  payload: { path: string[] };
}

export interface ISchemaInfo {
  name: string;
  fieldCount: number;
  connectedFields: number;
}
export interface IMappingSummaryType {
  mappingTitle?: string;
  sourceTitle?: string;
  targetTitle?: string;
  mapping?: number;
  transformation?: number;
}

export type MappingActions =
  | ISelected
  | InitMappingSchema
  | IUpdateDefault
  | IInitSummary
  // | IUpdateMappingSummary
  | UpdateMappingSchema
  | IConnectFields
  | IDisconnectFields
  | ICreateDanglingMapping
  | IDisconnectMapping
  | IRemoveMapping
  | IClearState
  | ICreateSourceProcessor
  | ICreateOtherSourceMapping
  | IUpdateTargetProperty
  | INewTargetProperty
  | IRemoveTargetProperty;

export interface IMappingState {
  locked: boolean; // mapping locked due to deployed package version
  uuid?: string; // uuid  of the mapping
  selected: undefined | ISelectedPayload; // Selected item
  sourceSchema: ISchema | null; // source schema
  sourceEntity?: IEntityType; // rest of the source DB entity
  targetSchema: ISchema | null; // target schema
  targetEntity?: IEntityType; // rest of the target DB entity
  mappingSchema: ICollectionType | null; // the actual mapping schema
  mappingEntity?: IEntityType; // rest of the mapping DB entity
  mappingSummary: IMappingSummaryType | null;
  structureEditor?: StructureEditor; // the editor helper. Might not want it here like this. but as useRef in control. but then i need a copy running in here
  structureHashId: string; // uuid changes when the structure changes, not a field value
  validationMessages: IPropertyBag;
}
export interface MappingContextInterface {
  state: IMappingState;
  dispatch: (MappingAction: MappingActions) => void;
}
export const MappingContext = React.createContext<MappingContextInterface | null>(null);
